/**
 * Navigation Configuration for Auxilio Finance
 * Defines sidebar and navigation menu items with collapsible categories
 */

import {
  HomeIcon,
  BookOpenIcon,
  CalculatorIcon,
  QuestionMarkCircleIcon,
  BanknotesIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  FolderIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  ReceiptPercentIcon,
  SparklesIcon,
  ChartBarIcon,
  FlagIcon,
  ClipboardDocumentListIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

export interface NavigationItem {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  descriptionKey?: string;
  releaseQuarter?: string;
  badgeKey?: string;
}

export interface NavigationCategory {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
  defaultExpanded?: boolean;
}

export const navigationCategories: NavigationCategory[] = [
  {
    id: 'general',
    labelKey: 'common:navigation.categories.general',
    icon: HomeIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'ask-questions',
        labelKey: 'common:navigation.items.askQuestions',
        path: '/ask-questions',
        icon: QuestionMarkCircleIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.askQuestions',
      },
      {
        id: 'financial-glossary',
        labelKey: 'common:navigation.items.financialGlossary',
        path: '/financial-glossary',
        icon: BookOpenIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.financialGlossary',
      },
      {
        id: 'calculators',
        labelKey: 'common:navigation.items.calculators',
        path: '/calculators',
        icon: CalculatorIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.calculators',
      },
      {
        id: 'financial-documents',
        labelKey: 'common:navigation.items.documents',
        path: '/documents',
        icon: FolderIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.documents',
      },
    ],
  },
  {
    id: 'tracking',
    labelKey: 'common:navigation.categories.tracking',
    icon: ClipboardDocumentListIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'budget',
        labelKey: 'common:navigation.items.budgetTracker',
        path: '/budget',
        icon: BanknotesIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.budgetTracker',
      },
      {
        id: 'net-worth',
        labelKey: 'common:navigation.items.netWorth',
        path: '/net-worth',
        icon: ChartBarIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.netWorth',
      },
      {
        id: 'debt',
        labelKey: 'common:navigation.items.debtManager',
        path: '/debt',
        icon: CreditCardIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.debtManager',
      },
    ],
  },
  {
    id: 'analysis',
    labelKey: 'common:navigation.categories.analysis',
    icon: PresentationChartLineIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'statements',
        labelKey: 'common:navigation.items.statementAnalyzer',
        path: '/statements',
        icon: DocumentTextIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.statementAnalyzer',
      },
      {
        id: 'portfolio',
        labelKey: 'common:navigation.items.portfolioAnalyzer',
        path: '/portfolio',
        icon: BriefcaseIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.portfolioAnalyzer',
      },
      {
        id: 'credit',
        labelKey: 'common:navigation.items.creditAnalyzer',
        path: '/credit',
        icon: CreditCardIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.creditAnalyzer',
      },
    ],
  },
  {
    id: 'planning',
    labelKey: 'common:navigation.categories.planning',
    icon: ArrowTrendingUpIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'loans',
        labelKey: 'common:navigation.items.loanPackage',
        path: '/loans',
        icon: DocumentDuplicateIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.loanPackage',
      },
      {
        id: 'taxes',
        labelKey: 'common:navigation.items.taxPlanning',
        path: '/taxes',
        icon: ReceiptPercentIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.taxPlanning',
      },
      {
        id: 'goals',
        labelKey: 'common:navigation.items.goals',
        path: '/goals',
        icon: FlagIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.goals',
      },
    ],
  },
  {
    id: 'resources',
    labelKey: 'common:navigation.categories.resources',
    icon: SparklesIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'ai-advisor',
        labelKey: 'common:navigation.items.aiAdvisor',
        path: '/advisor',
        icon: SparklesIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.aiAdvisor',
      },
    ],
  },
];

// Utility functions
export const getAllNavigationItems = (): NavigationItem[] => {
  return navigationCategories.flatMap((category) => category.items);
};

export const findNavigationItemByPath = (path: string): NavigationItem | undefined => {
  return getAllNavigationItems().find((item) => item.path === path);
};

export const getCategoryForPath = (path: string): NavigationCategory | undefined => {
  return navigationCategories.find((category) =>
    category.items.some((item) => item.path === path)
  );
};
